/**
 * MinimalTemplate - Ultra clean, foco no conteúdo, tipografia elegante
 * Layout single column com espaçamento generoso e pouquíssimas cores
 */

import { SantinhoTemplateProps, formatMandate, hasSocialLinks } from './types';
import { SocialLinks } from './SocialLinks';
import { GallerySection } from './GallerySection';
import { ApprovedProjectsSection } from './ApprovedProjectsSection';
import { ContactRegistrationButton } from '../ContactRegistrationButton';
import { WebchatWidget } from '../../webchat';

export function MinimalTemplate({ tenant }: SantinhoTemplateProps) {
  const { publicPage, theme, name } = tenant;
  const { title, description, content, socialLinks } = publicPage;

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      style={{ fontFamily: theme.fontFamily || 'inherit' }}
    >
      {/* Header */}
      <header className="pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            {/* Candidate Photo */}
            <div className="mb-10 flex justify-center">
              {content.candidatePhoto ? (
                <div className="relative">
                  <img
                    src={content.candidatePhoto}
                    alt={name}
                    className="w-36 h-36 md:w-44 md:h-44 rounded-full object-cover ring-1 ring-gray-200"
                  />
                  <div
                    className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white"
                    style={{ backgroundColor: theme.primaryColor }}
                  />
                </div>
              ) : (
                <div
                  className="w-36 h-36 md:w-44 md:h-44 rounded-full flex items-center justify-center text-5xl font-light text-white ring-1 ring-gray-200"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  {name.charAt(0)}
                </div>
              )}
            </div>

            {/* Candidate Number */}
            {content.candidateNumber && (
              <p
                className="text-6xl md:text-7xl font-light tracking-[0.15em] mb-4"
                style={{ color: theme.primaryColor }}
              >
                {content.candidateNumber}
              </p>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-5xl font-light text-gray-900 tracking-tight leading-tight">
              {title || name}
            </h1>

            {/* Description */}
            {description && (
              <p className="mt-5 text-lg text-gray-400 font-light leading-relaxed max-w-md mx-auto">
                {description}
              </p>
            )}

            {/* Minimal accent line */}
            <div className="flex items-center justify-center gap-2 mt-10">
              <div className="w-4 h-px" style={{ backgroundColor: theme.primaryColor }} />
              <div
                className="w-8 h-px"
                style={{ backgroundColor: theme.primaryColor, opacity: 0.6 }}
              />
              <div
                className="w-4 h-px"
                style={{ backgroundColor: theme.primaryColor, opacity: 0.3 }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Cover Photo */}
      {content.coverPhoto && (
        <div className="container mx-auto px-6 mb-20">
          <div className="max-w-3xl mx-auto">
            <img
              src={content.coverPhoto}
              alt={name}
              className="w-full h-64 md:h-[26rem] object-cover rounded-2xl"
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-6 pb-24">
        <div className="max-w-xl mx-auto space-y-20">

          {/* Biography */}
          {content.biography && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Sobre
              </h2>
              <div className="space-y-5">
                {content.biography.split('\n').filter(Boolean).map((p, i) => (
                  <p key={i} className="text-gray-500 leading-[1.9] font-light text-lg">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          )}

          {/* Mandates */}
          {content.mandates && content.mandates.length > 0 && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Experiência Política
              </h2>
              <ul className="space-y-4">
                {content.mandates.map((mandate, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 py-3 border-b border-gray-100 last:border-0"
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0"
                      style={{ backgroundColor: theme.primaryColor }}
                    />
                    <span className="text-gray-600 font-light text-lg leading-relaxed">
                      {formatMandate(mandate)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Proposals */}
          {content.proposals && content.proposals.length > 0 && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Propostas
              </h2>
              <ul className="space-y-6">
                {content.proposals.map((proposal, i) => (
                  <li key={i} className="flex items-start gap-5">
                    <span
                      className="flex-shrink-0 text-sm font-semibold tabular-nums mt-0.5"
                      style={{ color: theme.primaryColor }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <p className="text-gray-600 font-light leading-relaxed text-lg">{proposal}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Achievements */}
          {content.achievements && content.achievements.length > 0 && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Conquistas
              </h2>
              <ul className="space-y-5">
                {content.achievements.map((achievement, i) => (
                  <li key={i} className="flex items-start gap-4 text-gray-600 font-light text-lg">
                    <span
                      className="flex-shrink-0 font-medium text-base"
                      style={{ color: theme.primaryColor }}
                    >
                      —
                    </span>
                    <span className="leading-relaxed">{achievement}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Approved Projects */}
          {content.approvedProjects && content.approvedProjects.length > 0 && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Projetos Aprovados
              </h2>
              <ApprovedProjectsSection
                projects={content.approvedProjects}
                primaryColor={theme.primaryColor}
                variant="default"
              />
            </section>
          )}

          {/* Social Links */}
          {hasSocialLinks(socialLinks) && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Redes Sociais
              </h2>
              <SocialLinks links={socialLinks} color={theme.primaryColor} variant="vertical" />
            </section>
          )}

          {/* Gallery */}
          {content.gallery && content.gallery.length > 0 && (
            <section>
              <h2
                className="text-xs uppercase tracking-[0.2em] font-medium mb-8"
                style={{ color: theme.primaryColor }}
              >
                Galeria
              </h2>
              <GallerySection images={content.gallery} />
            </section>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-10 border-t border-gray-100">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-300 text-sm font-light">
            Página criada com{' '}
            <a
              href="https://campanhaflow.com.br"
              className="hover:underline"
              style={{ color: theme.primaryColor }}
            >
              CampanhaFlow
            </a>
          </p>
        </div>
      </footer>

      {/* Contact Registration Button */}
      <ContactRegistrationButton />

      {/* Webchat widget */}
      <WebchatWidget tenant={tenant} />
    </div>
  );
}
